import { SWS_HOST } from '@/constants';
import DocumentHeader from '@/components/DocumentHeader';
import DocumentSchema from '@/components/DocumentSchema';
import type {
  Corporation,
  ItemList,
  ListItem,
  SearchResultsPage,
  FAQPage,
} from 'schema-dts';

interface Company {
  canonicalUrl: string;
  companyId: string;
  shortDescription: string;
  mainThumb: string;
  exchangeSymbol: string;
  shortName: string;
  slug: string;
  snowflakePoints: [number, number, number, number, number];
  tickerSymbol: string;
  uniqueSymbol: string;
  industrySlug: string;
  industryLabel: string;
  legalName: string;
  logoUrl: string;
  yearFounded: string;
}

interface Props {
  active: boolean;
  canonicalUrl: string;
  metaTitle: string;
  question: string;
  answer: string;
  metaDescription: string;
  page: number;
  companies: Company[];
}

const SEO = ({
  metaTitle,
  metaDescription,
  page,
  question,
  answer,
  active = true,
  companies,
  canonicalUrl,
}: Props) => (
  <>
    <DocumentHeader
      title={metaTitle}
      allowCrawlers={companies.length > 0 && active}
      description={metaDescription}
    >
      {companies.length > 0 && (
        <link
          rel="canonical"
          href={`${SWS_HOST}${canonicalUrl}${
            page === 1 ? '' : '?page=' + page
          }`}
        />
      )}
    </DocumentHeader>
    {RUNTIME_ENV === 'server' && (
      <>
        <DocumentSchema<SearchResultsPage>
          item={{
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            identifier: `${SWS_HOST}${canonicalUrl}#webpage`,
            url: `${SWS_HOST}${canonicalUrl}`,
            mainEntity: [
              {
                '@type': 'ItemList',
                name: metaTitle,
                itemListOrder: 'http://schema.org/ItemListOrderAscending',
                itemListElement: companies.map((n, i) => {
                  return {
                    '@type': 'ListItem',
                    position: i + 1,
                    item: {
                      '@type': 'Corporation',
                      name: n.shortName,
                      legalName: n.legalName,
                      tickerSymbol: n.tickerSymbol,
                      url: `${SWS_HOST}${n.slug}`,
                      logo: n.logoUrl,
                      foundingDate: n.yearFounded,
                      description: n.shortDescription,
                    } as Corporation,
                  } as ListItem;
                }),
              } as ItemList,
            ],
            isPartOf: {
              '@type': 'WebSite',
              url: 'https://simplywall.st',
              name: 'Simply Wall St',
            },
          }}
        />
        <DocumentSchema<FAQPage>
          item={{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: {
              '@type': 'Question',
              name: question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: answer,
              },
            },
          }}
        />
      </>
    )}
  </>
);

export default SEO;
